import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import './index.scss';
import * as Yup from 'yup';
import { StringSchema } from 'yup';
import { useFormik } from 'formik';
import TextField from '../../../../../../../../design/1/js/templates/molecules/text-field';
import DatePickerField from '../../../../../../../../design/1/js/templates/molecules/date-picker-field';
import { IconName } from '../../../../../../../../design/1/js/templates/atoms/icon';
import FormButton, { FormButtonVariation } from '../../../../../../../../design/1/js/templates/atoms/form-fields/form-button';
import Paragraph from '../../../../../../../../design/1/js/templates/atoms/paragraph';
import WefResponseContext from '../../../lib/wef/WefResponseContext';
import { getIsoDate } from '../../../../../../../../design/1/js/lib/formatData';
import { baseIntegerSchema, cleanIntegerValue } from '../../../lib/yupFields/baseIntegerSchema';
import RadioButton from '../../../../../../../../design/1/js/templates/atoms/form-fields/radiobutton';

export interface WefSimulationFormProps {
    handleSubmit: (response: WefResponseContext) => void;
    headerId: string;
    hidden: boolean;
    maxWef: number;
}

const WEF_SHARE_CERTIFICATE_MANDATORY_BELOW = 20000;

const WefSimulationForm = ({
    handleSubmit, headerId, hidden, maxWef,
}: WefSimulationFormProps) => {
    const baseClassName = 'w-wefSimulationForm';
    const [datePickerError, setDatePickerError] = useState(null);
    const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
    const [displayShareCertificate, setDisplayShareCertificate] = useState(false);
    const [displayShareCertificateError, setDisplayShareCertificateError] = useState(false);
    const datePickerRef = useRef<HTMLDivElement>(null);

    const validationSchema = Yup.object().shape({
        wef: baseIntegerSchema()
            .max(maxWef, window.sv_resource.get('plf_simulation_errormsg_max_wef'))
        ,
        shareCertificate: Yup.string().required(window.sv_resource.get('form_errormsg_string')),
        // @ts-ignore -> Yup is actually able to handle strings as date; it casts strings with new Date(string)
        deadline: Yup.date()
            .typeError(window.sv_resource.get('form_errormsg_date'))
            .test('isThisYear', window.sv_resource.get('form_errormsg_date_this_year'), (val: Date) => val.getFullYear() === (new Date().getFullYear()))
            .test('isNotPast', window.sv_resource.get('form_errormsg_date_not_past'), (val: Date) => {
                const currentDate = new Date();
                currentDate.setHours(0, 0, 0, 0);
                return val >= currentDate;
            })
            .required(window.sv_resource.get('form_errormsg_date')) as StringSchema,
    });

    const initialValues = Object.freeze({
        wef: '',
        shareCertificate: '',
        deadline: getIsoDate(new Date()),
    });

    const onSubmit = ({ deadline, wef, shareCertificate }: typeof initialValues) => {
        if (wefValueIsBelowThreshold() && formik.values.shareCertificate.toString() !== 'shareCertificate-yes') {
            setDisplayShareCertificate(true);
            setDisplayShareCertificateError(true);

            return;
        }

        const numericWef = String(cleanIntegerValue(wef));
        // @ts-ignore
        shareCertificate = shareCertificate === 'shareCertificate-yes'
            ? 'shareCertificate-yes'
            : 'shareCertificate-no';

        handleSubmit({
            deadline,
            wef: numericWef,
            shareCertificate,
        });
    };

    const formik = useFormik({
        initialValues,
        onSubmit,
        validationSchema,
    });

    const handleIsValid = (isValid: boolean): void => {
        setDatePickerError(isValid ? null : window.sv_resource.get('form_errormsg_date_between_now_and_end_of_year'));
    };

    const wefValueIsBelowThreshold = (): boolean => {
        return cleanIntegerValue(formik.values.wef) < WEF_SHARE_CERTIFICATE_MANDATORY_BELOW;
    }

    const resetShareCertificateFields = (): void => {
        setDisplayShareCertificate(false)
        setDisplayShareCertificateError(false)
        formik.setFieldValue('shareCertificate', '-');
    };

    const validateWefInput = (wefValue: number): void => {
        if (wefValue < WEF_SHARE_CERTIFICATE_MANDATORY_BELOW) {
            setSubmitButtonDisabled(true);
            setDisplayShareCertificate(true)

            return;
        }

        resetShareCertificateFields();
        setSubmitButtonDisabled(false);
    }

    const handleShareCertificateChange = (event: ChangeEvent<HTMLElement>, isShareCertificate: boolean): void => {
        formik.handleChange(event);

        if (isShareCertificate) {
            setSubmitButtonDisabled(false);
            setDisplayShareCertificateError(false);
        } else {
            setSubmitButtonDisabled(true);
            setDisplayShareCertificateError(true);
        }
    }

    useEffect(() => {
        const wefValue = cleanIntegerValue(formik.values.wef);
        if (cleanIntegerValue(formik.values.wef) < 1) {
            // reset all if input is cleared, to avoid confusion when lower values are later entered in again
            // values e.g. 0.2 will be considered invalid, but should also trigger a reset, otherwise the share certificate question will remain visible
            resetShareCertificateFields();

            return;
        }
        // this debounce is used to avoid validating on each keystroke
        const timeout = setTimeout(() => {
            validateWefInput(wefValue)
        }, 1300);

        return () => clearTimeout(timeout);
    }, [formik.values.wef]);

    return (
        <div className={`${baseClassName} ${hidden ? 'd-none' : ''} `}>
            <h2 id={headerId}>{window.sv_resource.get('plf_simulation_title')}</h2>
            <Paragraph>{window.sv_resource.get('plf_simulation_wef_description')}</Paragraph>
            <form name="wefSimulation" onSubmit={formik.handleSubmit}>
                <>
                    <div className="row gap-xxs">
                        <TextField
                            className={`${baseClassName}__field col-12 col-lg-6`}
                            errors={formik.errors.wef}
                            icon={IconName.Chf}
                            id="wef"
                            label={window.sv_resource.get('plf_simulation_wef_label_advance')}
                            name="wef"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            onFocus={formik.handleBlur}
                            placeholder={formik.values.wef}
                            touched={!!formik.touched.wef}
                            type="text"
                            value={formik.values.wef}
                        />
                        <DatePickerField
                            className="col-12 col-lg-6"
                            errors={datePickerError ?? formik.errors.deadline}
                            icon={IconName.Calendar}
                            id="deadline"
                            label={window.sv_resource.get('plf_simulation_wef_label_date')}
                            maxDate={new Date(new Date().getFullYear(), 11, 31)}
                            minDate={new Date()}
                            ref={datePickerRef}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            onIsValid={handleIsValid}
                            touched
                            value={formik.values.deadline}
                        />
                        {displayShareCertificate && (
                            <>
                                <div className={`${baseClassName}__radioRow row gap-xxs`}>
                                    <Paragraph className="col-12 col-lg-6">{window.sv_resource.get('plf_simulation_wef_share_certificate_label')}</Paragraph>
                                    <div className={`${baseClassName}__radioWrapper col-12 col-lg-6`}>
                                        <RadioButton
                                            checked={formik.values.shareCertificate.toString() === 'shareCertificate-no'}
                                            errors={formik.errors.shareCertificate}
                                            id="shareCertificate-no"
                                            name="shareCertificate"
                                            onBlur={formik.handleBlur}
                                            onChange={(e) => handleShareCertificateChange(e, false)}
                                            touched={!!formik.touched.shareCertificate}
                                            label={window.sv_resource.get('plf_simulation_wef_share_certificate_option_no')}
                                        />
                                        <RadioButton
                                            checked={formik.values.shareCertificate.toString() === 'shareCertificate-yes'}
                                            errors={formik.errors.shareCertificate}
                                            id="shareCertificate-yes"
                                            name="shareCertificate"
                                            onBlur={formik.handleBlur}
                                            onChange={(e) => handleShareCertificateChange(e, true)}
                                            touched={!!formik.touched.shareCertificate}
                                            label={window.sv_resource.get('plf_simulation_wef_share_certificate_option_yes')}
                                        />
                                    </div>
                                </div>
                                {displayShareCertificateError && (
                                    <div className={`${baseClassName}__radioRow row gap-xxs`}>
                                        <Paragraph className="col-12 col-lg-6 a-textInput__msg">{window.sv_resource.get('plf_simulation_wef_advance_withdrawal_too_low')}</Paragraph>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className={`${baseClassName}__submitWrapper row`}>
                        <FormButton
                            disabled={submitButtonDisabled || Object.keys(formik.errors).length > 0 || Object.keys(formik.touched).length === 0 || datePickerError !== null}
                            hasIcon={false}
                            text={window.sv_resource.get('plf_simulation_calc_button_label')}
                            type="submit"
                            variation={FormButtonVariation.Primary}
                        />
                    </div>
                </>
            </form>
        </div>
    );
};

export default WefSimulationForm;
